import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import { AccountError, AccountSkeleton } from 'pages/booking/account/account-utils';
import { BOOKING_ACCOUNT_INVOICE_DETAIL_PATH } from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDate } from 'lang/date-time';

/**
 * Cursor-paginated booking invoice history.
 */
export default function AccountInvoices() {
  const { getAction } = useApi();
  const { _t, lang } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (append = false) => {
      setLoading(true);
      setError(false);
      try {
        const data = (await getAction('GetMyReservationInvoices')({
          query: { limit: 20, ...(append && nextCursor ? { cursor: nextCursor } : {}) } as any,
        })) as any;
        setRows((current) => (append ? [...current, ...data.invoices] : data.invoices));
        setNextCursor(data.nextCursor || null);
      } catch (_error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [getAction, nextCursor]
  );

  useEffect(() => {
    void load();
  }, []);

  return (
    <BookingAccountLayout>
      {loading && rows.length === 0 && <AccountSkeleton />}
      {error && <AccountError retry={() => void load()} />}
      {!loading && !error && rows.length === 0 && (
        <div className="account-state">
          <h2>{_t('No invoices yet')}</h2>
          <p>{_t('Booking invoices issued to you will appear here.')}</p>
        </div>
      )}
      <div className="account-card-list">
        {rows.map((invoice) => (
          <article className="account-invoice-card" key={invoice.id}>
            <div>
              <p className="account-eyebrow">
                {invoice.invoiceNumber || _t('Invoice pending')} ·{' '}
                {invoice.sourceType === 'credit-purchase'
                  ? `${_t('Credit purchase')} #${invoice.creditPurchaseId}`
                  : `${_t('Reservation')} #${invoice.reservationId}`}
              </p>
              <h2>{invoice.space.name}</h2>
              <p>{invoice.building.title}</p>
              <p>
                {_t('Issued')}: {formatLocaleDate(invoice.issueDate, lang)} · {_t('Due')}:{' '}
                {formatLocaleDate(invoice.dueDate, lang)}
              </p>
              <p>
                {_t('Status')}: {_t(invoice.issuanceStatus === 'issued' ? 'Issued' : invoice.issuanceStatus)} ·{' '}
                {_t('VAT')}: {Number(invoice.totalVat || 0).toFixed(2)} {invoice.currencyCode} · {_t('Gateway')}:{' '}
                {_t(invoice.paymentGateway === 'stripe' ? 'Stripe' : 'Manual')}
              </p>
            </div>
            <div className="account-invoice-total">
              <strong>
                {Number(invoice.totalPriceWithVat).toFixed(2)} {invoice.currencyCode}
              </strong>
              {invoice.issuanceStatus === 'issued' && (
                <button
                  className="account-button account-button--secondary"
                  onClick={() =>
                    navigate(BOOKING_ACCOUNT_INVOICE_DETAIL_PATH.insert({ invoiceId: String(invoice.id) }))
                  }
                >
                  {_t('View PDF')}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {nextCursor && (
        <button className="account-button account-load-more" disabled={loading} onClick={() => void load(true)}>
          {loading ? _t('Loading...') : _t('Load more')}
        </button>
      )}
    </BookingAccountLayout>
  );
}
